import React, { useState, useEffect, useCallback } from 'react';
import {
    Paper,
    Button,
    Typography,
    IconButton,
    Box,
    Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../config';

const MessageBanner = ({ user }) => {
    const [messages, setMessages] = useState([]);
    const [open, setOpen] = useState(false);

    const currentMessage = messages[0] || null;

    const fetchPending = useCallback(async () => {
        if (!user || !user.emp_id || !user.comp_id) return;
        try {
            const response = await axios.get(APIURL + '/messages/pending', {
                params: {
                    emp_id: user.emp_id,
                    comp_id: user.comp_id,
                    role: (user.type || '').toLowerCase(),
                },
            });
            if (response.data && response.data.length > 0) {
                setMessages(response.data);
                setOpen(true);
            }
        } catch (err) {
            console.error('MessageBanner: failed to fetch pending messages', err);
        }
    }, [user]);

    useEffect(() => {
        fetchPending();
    }, [fetchPending]);

    const handleClose = async () => {
        if (!currentMessage) return;
        try {
            await axios.put(`${APIURL}/messages/delivery/${currentMessage.delivery_id}`);
        } catch (err) {
            console.error('MessageBanner: failed to dismiss message', err);
            return;
        }
        setMessages((prevMessages) => {
            const remaining = prevMessages.slice(1);
            if (remaining.length === 0) {
                setOpen(false);
            }
            return remaining;
        });
    };

    if (!currentMessage) return null;

    const isCompany = currentMessage.msg_source === 'company';
    const accentColor = isCompany ? '#4caf50' : '#ff9800';

    return (
        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
            <Paper
                elevation={12}
                sx={{
                    position: 'fixed',
                    top: 5,
                    left: 0,
                    right: 0,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: '40%',
                    minWidth: 340,
                    maxWidth: 580,
                    zIndex: 1400,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#ffffff',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                {/* Accent bar */}
                <Box sx={{ height: '3px', background: `linear-gradient(90deg, ${accentColor}, ${accentColor}aa)` }} />

                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        pt: 1.5,
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                backgroundColor: `${accentColor}18`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <NotificationsNoneOutlinedIcon sx={{ fontSize: 16, color: accentColor }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', letterSpacing: 0.1 }}>
                            {isCompany ? 'A message from your agency' : 'Notice from FieldWorker'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {messages.length > 1 && (
                            <Box sx={{
                                px: 1, py: 0.25,
                                borderRadius: '20px',
                                backgroundColor: '#f0f0f0',
                                mr: 0.5,
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '0.68rem' }}>
                                    {messages.length} pending
                                </Typography>
                            </Box>
                        )}
                        <IconButton size="small" onClick={handleClose} sx={{ color: '#aaa', '&:hover': { color: '#555', backgroundColor: '#f5f5f5' } }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Message body */}
                <Box
                    sx={{
                        px: 2.5,
                        pb: 1.5,
                        pt: 0.5,
                        maxHeight: '5.5em',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: '#fafafa' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#bebebe', borderRadius: '22px' },
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                        {currentMessage.msg_text}
                    </Typography>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.25,
                        borderTop: '1px solid #f0f0f0',
                        backgroundColor: '#fafafa',
                    }}
                >
                    <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.68rem' }}>
                        {currentMessage.valid_until
                            ? `Expires ${moment(currentMessage.valid_until).format('MMM D, YYYY')}`
                            : `Posted ${moment(currentMessage.created_at).format('MMM D')}`}
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={handleClose}
                        sx={{
                            backgroundColor: accentColor,
                            color: '#fff',
                            borderRadius: '8px',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            px: 2,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: accentColor, filter: 'brightness(0.88)', boxShadow: 'none' },
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Paper>
        </Slide>
    );
};

export default MessageBanner;
